/**
 * Link de indicação e compartilhamento do Programa de recomendação: o link é o checkout da marca
 * com `#identificador` (o checkout grava o identificador num cookie de 30 dias e aplica o
 * desconto); as mensagens saem por WhatsApp, LinkedIn ou email.
 */
import { useEffect, useRef, useState } from 'react';
import { externalUrls } from '../../config/env';

export type Channel = 'whatsapp' | 'linkedin' | 'email';

export function referralLink(slug: string, source: string): { url: string; display: string } {
  const base = source === 'lead-machine' ? externalUrls.buyLeadMachine : externalUrls.buyEminer;
  const url = `${base.replace(/\/$/, '')}/#${slug}`;
  return { url, display: url.replace(/^https?:\/\//, '') };
}

/** Abre o canal com a mensagem. No LinkedIn a mensagem também vai para a área de transferência. */
export function sendMessage(channel: Channel, message: string, subject: string): void {
  if (channel === 'whatsapp') {
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener',
    );
  } else if (channel === 'linkedin') {
    void navigator.clipboard?.writeText(message).catch(() => undefined);
    window.open(
      `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener',
    );
  } else {
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
  }
}

/**
 * Copia e marca o botão como "Copiado" por 2 s (--permanencia-2000: tempo de leitura, igual com
 * "reduzir movimento"). Clicar de novo copia de novo e reinicia o tempo, sem nova animação.
 */
export function useCopied(): [string | null, (id: string, text: string) => void] {
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  function copy(id: string, text: string): void {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(id);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(
          () => setCopied((current) => (current === id ? null : current)),
          2000,
        );
      })
      .catch(() => undefined);
  }
  return [copied, copy];
}

/** Sugestão de identificador: a parte do email antes do @, só com letras, números e hífen. */
export function suggestSlug(email: string | undefined): string {
  const local = (email ?? '').split('@')[0] ?? '';
  return local
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 30);
}
